/**
 * Rechnungsdaten auf der Kontoseite (seit 07.10.2026, mit dem Wechsel zu Mollie).
 *
 * Stripe fragte Anschrift und USt-ID im Checkout ab. Mollie tut das nicht, und
 * seine Rechnungen verlangen Anschrift und Land; die Umsatzsteuer hängt an
 * Land, Firma und USt-ID (steuer.ts im Backend). Deshalb stehen die Daten hier,
 * vor dem ersten Kauf. Die USt-ID prüft der Server bei VIES.
 */

import { useState, type FormEvent } from 'react';
import { ApiFehler, speichereRechnungsdaten, type Rechnungsdaten as Daten, type Status } from '../api';
import { Abschnitt, Feld, Hinweis, KleinerKnopf, Kreisel, Panel } from '../ui';
import { sprache, t } from '../sprache';

/** Deutschland zuerst, dann die EU, dann die häufigsten Länder außerhalb. */
export const LAENDER: Array<[string, string]> = [
  ['DE', 'Deutschland'], ['AT', 'Österreich'], ['BE', 'Belgien'], ['BG', 'Bulgarien'], ['HR', 'Kroatien'], ['CY', 'Zypern'],
  ['CZ', 'Tschechien'], ['DK', 'Dänemark'], ['EE', 'Estland'], ['FI', 'Finnland'], ['FR', 'Frankreich'], ['GR', 'Griechenland'],
  ['HU', 'Ungarn'], ['IE', 'Irland'], ['IT', 'Italien'], ['LV', 'Lettland'], ['LT', 'Litauen'], ['LU', 'Luxemburg'],
  ['MT', 'Malta'], ['NL', 'Niederlande'], ['PL', 'Polen'], ['PT', 'Portugal'], ['RO', 'Rumänien'], ['SK', 'Slowakei'],
  ['SI', 'Slowenien'], ['ES', 'Spanien'], ['SE', 'Schweden'],
  ['CH', 'Schweiz'], ['LI', 'Liechtenstein'], ['NO', 'Norwegen'], ['GB', 'Vereinigtes Königreich'], ['US', 'USA'], ['CA', 'Kanada'], ['AU', 'Australien'],
];
/** Dieselben Länder auf Englisch, gleiche Reihenfolge. */
const LAENDER_EN: Record<string, string> = {
  DE: 'Germany', AT: 'Austria', BE: 'Belgium', BG: 'Bulgaria', HR: 'Croatia', CY: 'Cyprus',
  CZ: 'Czechia', DK: 'Denmark', EE: 'Estonia', FI: 'Finland', FR: 'France', GR: 'Greece',
  HU: 'Hungary', IE: 'Ireland', IT: 'Italy', LV: 'Latvia', LT: 'Lithuania', LU: 'Luxembourg',
  MT: 'Malta', NL: 'Netherlands', PL: 'Poland', PT: 'Portugal', RO: 'Romania', SK: 'Slovakia',
  SI: 'Slovenia', ES: 'Spain', SE: 'Sweden',
  CH: 'Switzerland', LI: 'Liechtenstein', NO: 'Norway', GB: 'United Kingdom', US: 'USA', CA: 'Canada', AU: 'Australia',
};
/** Die Länder in der Sprache der App. */
const laender = (): Array<[string, string]> =>
  sprache() === 'en' ? LAENDER.map(([k, n]) => [k, LAENDER_EN[k] ?? n]) : LAENDER;
const landName = (c: string) => laender().find(([k]) => k === c)?.[1] ?? c;

/** Was die Steuer für den Kunden heißt, in einem Satz. */
export function steuerSatz(s: NonNullable<Status['steuer']>): string {
  if (!s.erlaubt) return s.grund ?? t('An dieses Land können wir gerade noch nicht verkaufen.', 'We cannot sell to this country yet.');
  if (s.fall === 'inland') return t('Auf die Preise kommen 19 % Umsatzsteuer.', '19% VAT is added to the prices.');
  if (s.fall === 'reverse_charge') return t('Reverse Charge: wir berechnen keine Umsatzsteuer, du führst sie in deinem Land ab.', 'Reverse charge: we charge no VAT, you account for it in your country.');
  return t('Ohne deutsche Umsatzsteuer (Leistung außerhalb der EU).', 'No German VAT (service outside the EU).');
}

const leer: Daten = { art: 'privat', name: '', firma: '', strasse: '', plz: '', ort: '', land: 'DE', ust_id: '', registernummer: '' };

export default function Rechnungsdaten({ status, gespeichert }: { status: Status; gespeichert: () => void }) {
  const vorhanden = status.rechnungsdaten ?? null;
  const [bearbeiten, setBearbeiten] = useState(!vorhanden);
  const [d, setD] = useState<Daten>({ ...leer, ...(vorhanden ?? {}) });
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState('');
  const [hinweis, setHinweis] = useState('');
  const setze = (k: keyof Daten) => (e: { target: { value: string } }) => setD({ ...d, [k]: e.target.value });

  async function absenden(e: FormEvent) {
    e.preventDefault();
    setLaedt(true);
    setFehler('');
    setHinweis('');
    try {
      const r = await speichereRechnungsdaten(d);
      setHinweis(r.hinweis ?? '');
      setBearbeiten(false);
      gespeichert();
    } catch (err) {
      setFehler((err as ApiFehler).message);
    } finally {
      setLaedt(false);
    }
  }

  return (
    <div id="rechnungsdaten" className="mb-10 scroll-mt-4">
      <Abschnitt
        titel={t('Rechnungsdaten', 'Billing details')}
        unter={t('Stehen auf deinen Rechnungen. Für Firmen in der EU mit USt-ID rechnen wir ohne Umsatzsteuer ab (Reverse Charge).',
          'They appear on your invoices. For businesses in the EU with a VAT ID, we bill without VAT (reverse charge).')}
        aktion={vorhanden && !bearbeiten ? <KleinerKnopf type="button" onClick={() => setBearbeiten(true)}>{t('Ändern', 'Edit')}</KleinerKnopf> : undefined}
      >
        {hinweis && <Hinweis>{hinweis}</Hinweis>}
        {!bearbeiten && vorhanden ? (
          <Panel className="px-4 py-3.5 text-[0.9375rem] leading-relaxed text-text sm:px-5">
            {vorhanden.art === 'firma' && vorhanden.firma && <div className="font-semibold text-tinte">{vorhanden.firma}</div>}
            <div>{vorhanden.name}</div>
            <div>{vorhanden.strasse}, {vorhanden.plz} {vorhanden.ort}, {landName(vorhanden.land)}</div>
            {vorhanden.ust_id && (
              <div className="text-[0.8125rem] text-text-hell">
                {t('USt-ID', 'VAT ID')} {vorhanden.ust_id}{vorhanden.land !== 'DE' ? (vorhanden.ust_id_gueltig ? t(', geprüft', ', verified') : t(', nicht bestätigt', ', not confirmed')) : ''}
              </div>
            )}
            {status.steuer && (
              <div className={`mt-1.5 text-[0.8125rem] ${status.steuer.erlaubt ? 'text-text-hell' : 'text-schlecht'}`}>{steuerSatz(status.steuer)}</div>
            )}
          </Panel>
        ) : (
          <Panel className="px-4 pt-4 pb-1 sm:px-5">
            <form onSubmit={absenden} noValidate>
              {fehler && <Hinweis art="schlecht">{fehler}</Hinweis>}
              <fieldset className="m-0 mb-4 flex gap-5 border-0 p-0">
                <legend className="mb-1.5 text-[0.84375rem] font-medium text-text">{t('Du kaufst als', 'You are buying as')}</legend>
                {(['privat', 'firma'] as const).map(a => (
                  <label key={a} className="inline-flex cursor-pointer items-center gap-2 text-[0.9375rem]">
                    <input type="radio" name="art" value={a} checked={d.art === a} onChange={() => setD({ ...d, art: a })} disabled={laedt} />
                    {a === 'privat' ? t('Privatperson', 'Private individual') : t('Firma', 'Business')}
                  </label>
                ))}
              </fieldset>
              {d.art === 'firma' && <Feld label={t('Firma', 'Company')} name="organization" autoComplete="organization" value={d.firma ?? ''} onChange={setze('firma')} disabled={laedt} />}
              <Feld label={d.art === 'firma' ? t('Ansprechperson', 'Contact person') : 'Name'} name="name" autoComplete="name" value={d.name} onChange={setze('name')} disabled={laedt} />
              <Feld label={t('Straße und Hausnummer', 'Street and number')} name="street" autoComplete="street-address" value={d.strasse} onChange={setze('strasse')} disabled={laedt} />
              <div className="grid grid-cols-[minmax(0,7rem)_minmax(0,1fr)] gap-3">
                <Feld label={t('PLZ', 'Postcode')} name="postal" autoComplete="postal-code" value={d.plz} onChange={setze('plz')} disabled={laedt} />
                <Feld label={t('Ort', 'City')} name="city" autoComplete="address-level2" value={d.ort} onChange={setze('ort')} disabled={laedt} />
              </div>
              <label className="mb-4 block">
                <span className="mb-1.5 block text-[0.84375rem] font-medium text-text">{t('Land', 'Country')}</span>
                <select
                  name="country" autoComplete="country" value={d.land} onChange={setze('land')} disabled={laedt}
                  className="h-[2.75rem] w-full rounded-lg border border-linie bg-flaeche px-3 text-[0.9375rem] text-text"
                >
                  {laender().map(([k, n]) => <option key={k} value={k}>{n}</option>)}
                </select>
              </label>
              {d.art === 'firma' && (
                <>
                  <Feld
                    label={t('USt-ID', 'VAT ID')} name="vat" value={d.ust_id ?? ''} onChange={setze('ust_id')} disabled={laedt}
                    hinweis={d.land === 'DE'
                      ? t('Optional. Ohne USt-ID braucht es die Registernummer.', 'Optional. Without a VAT ID, the registration number is needed.')
                      : t('Mit gültiger USt-ID ohne Umsatzsteuer (Reverse Charge). Wir prüfen sie bei VIES.', 'With a valid VAT ID, no VAT (reverse charge). We check it with VIES.')}
                  />
                  {!d.ust_id && (
                    <Feld label={t('Registernummer (z. B. HRB 12345)', 'Registration number (e.g. HRB 12345)')} name="registry" value={d.registernummer ?? ''} onChange={setze('registernummer')} disabled={laedt} />
                  )}
                </>
              )}
              <div className="mb-4 flex flex-wrap items-center gap-2">
                <KleinerKnopf type="submit" disabled={laedt} className="!border-knopf !bg-knopf !text-auf-marke hover:!bg-knopf-hover">
                  {laedt ? <><Kreisel className="mr-1.5 size-3" />{t('Wird gespeichert …', 'Saving …')}</> : t('Speichern', 'Save')}
                </KleinerKnopf>
                {vorhanden && <KleinerKnopf type="button" onClick={() => { setD({ ...leer, ...vorhanden }); setBearbeiten(false); }} disabled={laedt}>{t('Abbrechen', 'Cancel')}</KleinerKnopf>}
              </div>
            </form>
          </Panel>
        )}
      </Abschnitt>
    </div>
  );
}
